import React, { useState } from 'react';
import { updateConnectAccount } from '../../utils/stripe';
import './StripeOnboarding.css';

const StripeOnboarding = ({ accountId, onComplete, onCancel, userProfile }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Personal Information - pre-populate from user profile
    firstName: userProfile?.firstName || '',
    lastName: userProfile?.lastName || '',
    dob: {
      day: '',
      month: '',
      year: ''
    },
    phone: '',
    email: userProfile?.email || '',
    
    // Address Information
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: ''
    },
    
    // Tax Information
    ssn_last_4: '',
    
    // Bank Account
    bankAccount: {
      routing_number: '',
      account_number: '',
      account_holder_type: 'individual',
      bank_name: '',
      account_type: 'checking'
    }
  });

  const steps = [
    { number: 1, title: 'Personal Information', description: 'Basic personal details' },
    { number: 2, title: 'Address', description: 'Your address information' },
    { number: 3, title: 'Tax Information', description: 'Required for tax reporting' },
    { number: 4, title: 'Bank Account', description: 'Where you\'ll receive payments' }
  ];

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.firstName && formData.lastName && formData.dob.day && 
               formData.dob.month && formData.dob.year && formData.phone && formData.email;
      case 2:
        return formData.address.line1 && formData.address.city && 
               formData.address.state && formData.address.postal_code;
      case 3:
        return formData.ssn_last_4 && formData.ssn_last_4.length === 4;
      case 4:
        return formData.bankAccount.routing_number && 
               formData.bankAccount.routing_number.length === 9 &&
               formData.bankAccount.account_number &&
               formData.bankAccount.account_number.length >= 4 &&
               formData.bankAccount.bank_name &&
               formData.bankAccount.account_type;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Mask the account number for storage (keep only last 4 digits)
      const accountNumber = formData.bankAccount.account_number;
      const maskedAccountNumber = '*'.repeat(Math.max(0, accountNumber.length - 4)) + accountNumber.slice(-4);
      
      const stripeAccountData = {
        business_type: 'individual',
        individual: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          dob: {
            day: parseInt(formData.dob.day),
            month: parseInt(formData.dob.month),
            year: parseInt(formData.dob.year)
          },
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          ssn_last_4: formData.ssn_last_4
        },
        business_profile: {
          name: `${formData.firstName} ${formData.lastName}`,
          url: 'http://practicetalking.co/'
        },
        external_account: {
          object: 'bank_account',
          country: 'US',
          currency: 'usd',
          routing_number: formData.bankAccount.routing_number,
          account_number: formData.bankAccount.account_number,
          account_holder_type: formData.bankAccount.account_holder_type
        },
        tos_acceptance: {
          date: Math.floor(Date.now() / 1000),
          ip: '127.0.0.1' // This should be the user's actual IP in production
        },
        // Bank account details for Firebase storage
        bankAccountDetails: {
          bank_name: formData.bankAccount.bank_name,
          account_type: formData.bankAccount.account_type,
          routing_number: formData.bankAccount.routing_number,
          account_number_masked: maskedAccountNumber,
          account_number_last4: accountNumber.slice(-4)
        }
      };

      console.log('Form data being submitted:', formData);
      console.log('Stripe account data being sent:', stripeAccountData);

      await updateConnectAccount(accountId, stripeAccountData);
      onComplete();
    } catch (error) {
      console.error('Error completing onboarding:', error);
      
      // Show specific error messages based on error type
      let errorMessage = 'Error completing setup. Please try again.';
      if (error.message.includes('routing_number_invalid') || error.message.includes('Invalid routing number')) {
        errorMessage = 'Invalid bank routing number. For testing, please use routing number: 110000000 and account number: 000123456789';
      } else if (error.message.includes('account_number_invalid')) {
        errorMessage = 'Invalid bank account number. Please check and enter a valid account number.';
      } else if (error.message.includes('ssn')) {
        errorMessage = 'Invalid SSN information. Please check your entries.';
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <h3>Personal Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  required
                />
              </div>
              <div className="form-group full-width">
                <label>Date of Birth *</label>
                <div className="dob-inputs">
                  <select
                    value={formData.dob.month}
                    onChange={(e) => handleInputChange('dob.month', e.target.value)}
                    required
                  >
                    <option value="">Month</option>
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(2000, i, 1).toLocaleString('en', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                  <select
                    value={formData.dob.day}
                    onChange={(e) => handleInputChange('dob.day', e.target.value)}
                    required
                  >
                    <option value="">Day</option>
                    {Array.from({ length: 31 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                  <select
                    value={formData.dob.year}
                    onChange={(e) => handleInputChange('dob.year', e.target.value)}
                    required
                  >
                    <option value="">Year</option>
                    {Array.from({ length: 100 }, (_, i) => {
                      const year = new Date().getFullYear() - 18 - i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <h3>Address Information</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Street Address *</label>
                <input
                  type="text"
                  value={formData.address.line1}
                  onChange={(e) => handleInputChange('address.line1', e.target.value)}
                  required
                />
              </div>
              <div className="form-group full-width">
                <label>Apartment, suite, etc. (optional)</label>
                <input
                  type="text"
                  value={formData.address.line2}
                  onChange={(e) => handleInputChange('address.line2', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  value={formData.address.city}
                  onChange={(e) => handleInputChange('address.city', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>State *</label>
                <select
                  value={formData.address.state}
                  onChange={(e) => handleInputChange('address.state', e.target.value)}
                  required
                >
                  <option value="">Select State</option>
                  <option value="AL">Alabama</option>
                  <option value="AK">Alaska</option>
                  <option value="AZ">Arizona</option>
                  <option value="AR">Arkansas</option>
                  <option value="CA">California</option>
                  <option value="CO">Colorado</option>
                  <option value="CT">Connecticut</option>
                  <option value="DE">Delaware</option>
                  <option value="FL">Florida</option>
                  <option value="GA">Georgia</option>
                  <option value="HI">Hawaii</option>
                  <option value="ID">Idaho</option>
                  <option value="IL">Illinois</option>
                  <option value="IN">Indiana</option>
                  <option value="IA">Iowa</option>
                  <option value="KS">Kansas</option>
                  <option value="KY">Kentucky</option>
                  <option value="LA">Louisiana</option>
                  <option value="ME">Maine</option>
                  <option value="MD">Maryland</option>
                  <option value="MA">Massachusetts</option>
                  <option value="MI">Michigan</option>
                  <option value="MN">Minnesota</option>
                  <option value="MS">Mississippi</option>
                  <option value="MO">Missouri</option>
                  <option value="MT">Montana</option>
                  <option value="NE">Nebraska</option>
                  <option value="NV">Nevada</option>
                  <option value="NH">New Hampshire</option>
                  <option value="NJ">New Jersey</option>
                  <option value="NM">New Mexico</option>
                  <option value="NY">New York</option>
                  <option value="NC">North Carolina</option>
                  <option value="ND">North Dakota</option>
                  <option value="OH">Ohio</option>
                  <option value="OK">Oklahoma</option>
                  <option value="OR">Oregon</option>
                  <option value="PA">Pennsylvania</option>
                  <option value="RI">Rhode Island</option>
                  <option value="SC">South Carolina</option>
                  <option value="SD">South Dakota</option>
                  <option value="TN">Tennessee</option>
                  <option value="TX">Texas</option>
                  <option value="UT">Utah</option>
                  <option value="VT">Vermont</option>
                  <option value="VA">Virginia</option>
                  <option value="WA">Washington</option>
                  <option value="WV">West Virginia</option>
                  <option value="WI">Wisconsin</option>
                  <option value="WY">Wyoming</option>
                </select>
              </div>
              <div className="form-group">
                <label>ZIP Code *</label>
                <input
                  type="text"
                  value={formData.address.postal_code}
                  onChange={(e) => handleInputChange('address.postal_code', e.target.value)}
                  required
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <h3>Tax Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Last 4 digits of SSN *</label>
                <input
                  type="text"
                  maxLength="4"
                  value={formData.ssn_last_4}
                  onChange={(e) => handleInputChange('ssn_last_4', e.target.value.replace(/\D/g, ''))}
                  placeholder="1234"
                  required
                />
                <small>Required for tax reporting purposes</small>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="step-content">
            <h3>Bank Account</h3>
            <p>Where you'll receive your payments</p>
            <div className="form-grid">
              <div className="form-group">
                <label>Bank Name *</label>
                <input
                  type="text"
                  value={formData.bankAccount.bank_name}
                  onChange={(e) => handleInputChange('bankAccount.bank_name', e.target.value)}
                  placeholder="Bank of America, Chase, Wells Fargo, etc."
                  required
                />
                <small>Name of your bank</small>
              </div>
              <div className="form-group">
                <label>Account Type *</label>
                <select
                  value={formData.bankAccount.account_type}
                  onChange={(e) => handleInputChange('bankAccount.account_type', e.target.value)}
                  required
                >
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                </select>
                <small>Type of bank account</small>
              </div>
              <div className="form-group">
                <label>Routing Number *</label>
                <input
                  type="text"
                  maxLength="9"
                  value={formData.bankAccount.routing_number}
                  onChange={(e) => handleInputChange('bankAccount.routing_number', e.target.value.replace(/\D/g, ''))}
                  placeholder="110000000"
                  required
                />
                <small>9-digit routing number. For testing, use: 110000000</small>
              </div>
              <div className="form-group">
                <label>Account Number *</label>
                <input
                  type="text"
                  value={formData.bankAccount.account_number}
                  onChange={(e) => handleInputChange('bankAccount.account_number', e.target.value.replace(/\D/g, ''))}
                  placeholder="000123456789"
                  required
                />
                <small>Your account number. For testing, use: 000123456789</small>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="stripe-onboarding-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Set Up Your Payment Account</h2>
          <button onClick={onCancel} className="close-btn">×</button>
        </div>

        <div className="progress-bar">
          <div className="steps">
            {steps.map((step) => (
              <div
                key={step.number}
                className={`step ${currentStep >= step.number ? 'active' : ''} ${
                  currentStep > step.number ? 'completed' : ''
                }`}
              >
                <div className="step-number">{step.number}</div>
                <div className="step-info">
                  <div className="step-title">{step.title}</div>
                  <div className="step-description">{step.description}</div>
                </div>
              </div>
            ))}
          </div>
          <div
            className="progress-fill"
            style={{ width: `${(currentStep / steps.length) * 100}%` }}
          />
        </div>

        <div className="modal-body">
          {renderStepContent()}
        </div>

        <div className="modal-footer">
          <div className="button-group">
            {currentStep > 1 && (
              <button onClick={handleBack} className="btn-secondary">
                Back
              </button>
            )}
            <button onClick={onCancel} className="btn-cancel">
              Cancel
            </button>
            {currentStep < steps.length ? (
              <button
                onClick={handleNext}
                disabled={!validateStep(currentStep)}
                className="btn-primary"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!validateStep(currentStep) || loading}
                className="btn-primary"
              >
                {loading ? 'Setting up...' : 'Complete Setup'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripeOnboarding;